<template>
  <div
    v-if="currentUser"
    class="py-2"
  >

    <BSidebar
      id="sbDgsReportGrowers"
      backdrop-variant="dark"
      backdrop
      shadow
      right
    >
      <template #title>
        <i class="fad fa-filter-list" /> Filters
      </template>

      <BContainer>
        <BRow>

          <BCol
            cols="12"
          >
            <BFormGroup
              label="Signed From"
              label-for="filterDateFrom"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <BFormInput
                id="filterDateFrom"
                v-model="tableHistory.filter.signed_from"
                debounce="1000"
                type="date"
                :disabled="tableHistory.busy"
              />
            </BFormGroup>
          </BCol>

          <BCol
            cols="12"
          >
            <BFormGroup
              label="Signed To"
              label-for="filterDateTo"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <BFormInput
                id="filterDateTo"
                v-model="tableHistory.filter.signed_to"
                debounce="1000"
                type="date"
                :disabled="tableHistory.busy"
              />
            </BFormGroup>
          </BCol>

          <BCol
            cols="12"
          >
            <BFormGroup
              label="Memo"
              label-for="filterMemo"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="tableHistory.selected.memo"
                placeholder="filter memo here"
                input-id="filterMemo"
                label="memo_name"
                class="border border-light rounded-lg"
                clearable
                append-to-body
                :options="(tableHistory.options.memos)"
                :loading="(tableHistory.fetching.memos)"
                :disabled="(tableHistory.busy || tableHistory.fetching.memos)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterMemos"
              >
                <template #selected-option="{ memo_name }">
                  <div
                    class="text-break text-wrap text-truncate"
                    style="font-size: 13px;"
                    v-text="memo_name"
                  />
                </template>

                <template #option="{ memo_name, team, creator }">
                  <div class="pb-1">
                    <div
                      class="text-break text-wrap"
                      style="font-size: 13px;"
                      v-text="memo_name"
                    />
                    <div
                      v-if="team?.id && creator?.id"
                      style="font-size: 10px;"
                      class="font-weight-bolder"
                    >
                      {{ getFullName(null, null, creator) }} - {{ team.team_name }}
                    </div>
                  </div>
                </template>

                <template #no-options="">
                  no available memo
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>

          <BCol
            cols="12"
          >
            <BFormGroup
              label="Customer"
              label-for="filterCustomer"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="tableHistory.selected.customer"
                placeholder="filter customer here"
                input-id="filterCustomer"
                label="customer_name"
                class="border border-light rounded-lg"
                clearable
                append-to-body
                :options="(tableHistory.options.customers)"
                :loading="(tableHistory.fetching.customers)"
                :disabled="(tableHistory.busy || tableHistory.fetching.customers)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterCustomers"
              >
                <template #selected-option="{ customer_name }">
                  <div
                    class="text-break text-wrap text-truncate"
                    style="font-size: 13px;"
                    v-text="customer_name"
                  />
                </template>

                <template #option="{ customer_name }">
                  <div
                    class="text-break text-wrap"
                    style="font-size: 13px;"
                    v-text="customer_name"
                  />
                </template>

                <template #no-options="">
                  no available customer
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>

        </BRow>
      </BContainer>
    </BSidebar>

    <BRow
      class="gap-y-2"
    >
      <BCol
        cols="12"
      >
        <BInputGroup>
          <BFormInput
            id="filterSearch"
            v-model="tableHistory.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
          <BInputGroupAppend>
            <BButton
              v-b-toggle.sbDgsReportGrowers
              variant="primary"
            >
              <i
                v-if="[settingFilters].some(isTrue => isTrue)"
                class="fad fa-spinner fa-spin p-0"
              />
              <i
                v-else
                class="fad fa-filter-list"
              /> Filters
            </BButton>
          </BInputGroupAppend>
        </BInputGroup>
      </BCol>
      <BCol>
        <BOverlay
          rounded
          opacity="0.1"
          spinner-small
          class="d-inline"
          spinner-variant="warning"
          :show="exporter.excel.exporting"
          style="margin-bottom: 1.1px;"
        >
          <JsonExcel
            :class="[
              'btn btn-sm btn-primary', {
                'btn-outline-primary disabled': exporter.excel.exporting || !isValidForExport
              }
            ]"
            :fetch="excelProvider"
            :before-generate="onStartExcelExport"
            :before-finish="onFinishExcelExport"
            :name="excelFileName"
            :fields="exporter.excel.fields"
            :escape-csv="exporter.excel.escape"
            :type="exporter.excel.type"
            :header="exporter.excel.header"
            :footer="exporter.excel.footer"
            worksheet="Monthly Plans"
          >
            {{ exporter.excel.exporting ? 'Exporting' : 'Export As Excel' }}
          </JsonExcel>
        </BOverlay>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableHistory"
      class="mt-2"
      :provider="tableProvider"
      :fields="tableHistory.fields"
      :filter="tableHistory.filter"
      :filter-on="tableHistory.filterOn"
      :sort-by="tableHistory.sortBy"
      :sort-desc="tableHistory.sortDesc"
      :sort-direction="tableHistory.sortDirection"
      :total-rows="tableHistory.totalRows"
      :current-page="tableHistory.currentPage"
      :per-page="tableHistory.perPage"
      :stacked="tableHistory.stacked"
      :busy="tableHistory.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            @click.prevent="() => $emit('view:row', item)"
          >
            View
          </BButton>
        </div>
      </template>

      <template #cell(assignee)="{ item }">
        <div class="d-flex flex-column gap-1 p-1">
          <div class="font-weight-bolder">
            {{ getFullName(null, null, item.assignee) }}
          </div>
          <div style="font-size: 10px;">
            {{ Array.from(item.assignee.clusters, cluster => cluster.cluster_name).join(', ') || 'n/a' }}
          </div>
        </div>
      </template>

      <template #cell(memo.memo_name)="{ value }">
        <div class="d-flex flex-column gap-1 p-1 text-break">
          {{ value }}
        </div>
      </template>

      <template #cell(customer)="{ item }">
        <div class="d-flex flex-column gap-1 p-1">
          <div class="font-weight-bolder">
            {{ item.customer.customer_name }}
          </div>
          <div style="font-size: 10px;">
            {{ item.customer.address || 'n/a' }}
          </div>
        </div>
      </template>

      <template #cell(is_signed)="{ item, value }">
        <div :class="`font-weight-bold text-${Number(item.is_signed) === 1 ? 'primary' : 'danger'}`">
          {{ value }}
        </div>
      </template>

      <template #cell(status)="{ value }">
        <div :class="`font-weight-bold text-${value === 'Closed' ? 'primary' : 'danger'}`">
          {{ value }}
        </div>
      </template>

      <template #cell(verification_status)="{ value }">
        <div :class="`font-weight-bold text-${value === 'Valid' ? 'primary' : (value === 'Invalid' ? 'danger' : 'dark')}`">
          {{ value }}
        </div>
      </template>
    </CustomTable>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'

import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'
import moment from 'moment'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import MCustomers from '@/mixins/accounts/customers'
import MMemos from '@/mixins/maintenances/open-memos'

import { SAccountCustomerMemo } from '@/services/accountant'

// import { find } from 'lodash'

export default {
  name: 'AccountantCustomerMemosHistory',

  mixins: [
    MxMisc,
    MxFormatter,
    MCustomers,
    MMemos
  ],

  emits: [
    'create:row',
    'edit:row',
    'verify:row',
    'view:row'
  ],

  data () {
    return {
      api: {},
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      state: {
        busy: false,
        editing: false
      },
      exporter: {
        excel: {
          exporting: false,
          header: [],
          escape: false,
          type: 'xls',
          fields: {
            Month: {
              field: '',
              callback: row => `="${row.schedule}"`
            },
            'Date Signed': {
              field: '',
              callback: row => `="${row.event_date}"`
            },
            Team: {
              field: 'memo.team',
              callback: team => team?.team_name
            },
            Memo: {
              field: 'memo',
              callback: memo => memo?.memo_name
            },
            Publisher: {
              field: 'memo.creator',
              callback: creator => `${creator?.first_name} ${creator?.last_name}`
            },
            Customer: {
              field: 'customer',
              callback: customer => customer?.customer_name
            },
            Cluster: {
              field: 'customer',
              callback: customer => customer?.cluster?.cluster_name || ''
            },
            'Area Manager': {
              field: 'creator',
              callback: creator => `${creator?.first_name} ${creator?.last_name}`
            },
            'Technical Officer': {
              field: 'assignee',
              callback: assignee => `${assignee?.first_name} ${assignee?.last_name}`
            },
            'Verified At': {
              field: 'updated_at',
              callback: updatedAt => `="${this.dateTimeFormatter(updatedAt)}"`
            }
          },
          footer: []
        }
      },
      tableHistory: {
        busy: false,
        fetching: {
          memos: false,
          customers: false
        },
        options: {
          memos: [],
          customers: [],
          verifications: [
            { value: 'all', text: 'All' },
            { value: 'Valid', text: 'Valid' },
            { value: 'Invalid', text: 'Invalid' }
          ]
        },
        selected: {
          memo: '',
          customer: '',
          team: {
            id: ''
          }
        },
        filter: {
          memo: '',
          customer: '',
          search: '',
          signed_from: '',
          signed_to: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-right stacked-full text-md-center' },
          { mobile: 9, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 5, key: 'event_date', label: 'Date Signed', class: 'min-w-125px', formatter: this.dateShortFormatter, sortable: true },
          { mobile: 1, key: 'memo.team.team_name', label: 'Team', class: 'min-w-md-225px', sortable: true },
          { mobile: 2, key: 'memo.memo_name', label: 'Memo', class: 'min-w-md-350px', sortable: true },
          { mobile: 3, key: 'memo.creator', label: 'Publisher', class: 'min-w-md-225px', formatter: creator => this.getFullName(null, null, creator), sortable: true },
          { mobile: 4, key: 'customer', label: 'Customer', class: 'min-w-md-350px', sortable: true },
          { mobile: 5, key: 'customer.cluster.cluster_name', label: 'Cluster', class: 'min-w-md-150px', sortable: true },
          { mobile: 6, key: 'creator', label: 'Area Manager', class: 'min-w-md-225px', formatter: creator => this.getFullName(null, null, creator), sortable: true },
          { mobile: 7, key: 'assignee', label: 'Technical Officer', class: 'min-w-md-225px', formatter: assignee => this.getFullName(null, null, assignee), sortable: true },
          { mobile: 8, key: 'updated_at', label: 'Verified At', class: 'min-w-125px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    excelFileName () {
      const closedFrom = moment(this.tableHistory.filter.signed_from).format('YYYY-MM-DD')
      const closedTo = moment(this.tableHistory.filter.signed_to).format('YYYY-MM-DD')

      return `Customer Memos ${closedFrom} - ${closedTo}`
    },

    isValidForExport () {
      const isDateFromNotEmpty = ![null, '', ' '].includes(this.tableHistory.filter.signed_from)
      const isDateToNotEmpty = ![null, '', ' '].includes(this.tableHistory.filter.signed_to)

      return isDateFromNotEmpty && isDateToNotEmpty && Number(this.tableHistory.totalRows) >= 1
    },

    settingFilters () {
      return this.tableHistory.fetching.memos || this.tableHistory.fetching.customers
    }
  },

  watch: {
    'tableHistory.selected.customer' (customer) {
      this.tableHistory.filter.customer = customer?.id || ''
    },

    'tableHistory.selected.memo' (memo) {
      this.tableHistory.filter.memo = memo?.id || ''
    }
  },

  mounted () {
    this.api = this.$refs.tableHistory

    if (this.currentUser) {
      this.getMemos(this.tableHistory).finally(() => {
        this.getCustomers(this.tableHistory)
      })
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableHistory.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        memo: filter.memo,
        customer: filter.customer,
        signed_from: filter.signed_from,
        signed_to: filter.signed_to
      }

      return SAccountCustomerMemo.get(payload).then(
        ({ data }) => {
          this.tableHistory.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableHistory.totalRows = 0
        return []
      }).finally(() => {
        this.tableHistory.busy = false
      })
    },

    searchFilterMemos (search, loading) {
      this.searchMemosProvider(this.tableHistory, search, loading)
    },

    searchFilterCustomers (search, loading) {
      this.searchCustomersProvider(this.tableHistory, search, loading)
    },
    async excelProvider () {
      if (this.exporter.busy || !this.isValidForExport) {
        return null
      }

      this.exporter.busy = this.exporter.excel.exporting = true

      const payload = {
        filter_text: this.tableHistory.filter.search,
        memo: this.tableHistory.filter.memo,
        customer: this.tableHistory.filter.customer,
        signed_from: this.tableHistory.filter.signed_from,
        signed_to: this.tableHistory.filter.signed_to
      }

      return SAccountCustomerMemo.exports(payload).then(
        ({ data: { items } }) => {
          if (items.length <= 0) {
            this.swalInvalid(
              'No available data to be Exported'
            )
          }

          return items
        }
      ).catch(() => {
        return []
      }).finally(() => {
        this.exporter.busy = this.exporter.excel.exporting = false
      })
    },

    onStartExcelExport () {
      if (this.exporter.busy || !this.isValidForExport) {
        return null
      }

      this.exporter.excel.header = []
      this.exporter.excel.footer = []
    },

    onFinishExcelExport () {
    }

  }
}
</script>
